import React, { useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Grid,
  IconButton,
  Paper,
  Divider,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  PlayArrow as RunIcon,
  GetApp as ExportIcon,
  AutoAwesome as AIIcon,
  Build as ManualIcon,
} from '@mui/icons-material';
import reportingService, { QueryFilter, ReportQuery, ReportResult, AIQuerySuggestion } from '../../services/reportingService';
import AIQueryAssistant from '../ai/AIQueryAssistant';

interface QueryBuilderProps {
  onReportGenerated: (result: ReportResult) => void;
  onError: (error: string) => void;
}

const QueryBuilder: React.FC<QueryBuilderProps> = ({ onReportGenerated, onError }) => {
  const [query, setQuery] = useState<ReportQuery>({
    entity: 'form_submissions',
    filters: [],
    groupBy: [],
    aggregations: [],
  });
  const [loading, setLoading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // 0 = AI, 1 = Manual
  const [aiSuggestions, setAiSuggestions] = useState<AIQuerySuggestion[]>([]);
  const [aiExplanation, setAiExplanation] = useState<string>('');

  const entityOptions = [
    { value: 'form_submissions', label: 'Form Submissions' },
    { value: 'clients', label: 'Clients' },
    { value: 'loans', label: 'Loans' },
  ];

  const operatorOptions = [
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Not Equals' },
    { value: 'contains', label: 'Contains' },
    { value: 'not_contains', label: 'Does Not Contain' },
    { value: 'greater_than', label: 'Greater Than' },
    { value: 'less_than', label: 'Less Than' },
    { value: 'between', label: 'Between' },
    { value: 'in', label: 'In List' },
    { value: 'not_in', label: 'Not In List' },
  ];

  const aggregationOptions = [
    { value: 'count', label: 'Count' },
    { value: 'sum', label: 'Sum' },
    { value: 'avg', label: 'Average' },
    { value: 'min', label: 'Minimum' },
    { value: 'max', label: 'Maximum' },
  ];

  const availableFields = reportingService.getAvailableFields(query.entity);

  const addFilter = useCallback(() => {
    setQuery(prev => ({
      ...prev,
      filters: [...prev.filters, { field: '', operator: 'equals', value: '' }]
    }));
  }, []);

  const updateFilter = useCallback((index: number, updates: Partial<QueryFilter>) => {
    setQuery(prev => ({
      ...prev,
      filters: prev.filters.map((filter, i) => 
        i === index ? { ...filter, ...updates } : filter
      )
    }));
  }, []);

  const removeFilter = useCallback((index: number) => {
    setQuery(prev => ({
      ...prev,
      filters: prev.filters.filter((_, i) => i !== index)
    }));
  }, []);

  const addAggregation = useCallback(() => {
    setQuery(prev => ({
      ...prev,
      aggregations: [...(prev.aggregations || []), { field: '', function: 'count' as const }]
    }));
  }, []);

  const updateAggregation = useCallback((index: number, updates: Record<string, unknown>) => {
    setQuery(prev => ({
      ...prev,
      aggregations: (prev.aggregations || []).map((agg, i) => 
        i === index ? { ...agg, ...updates } : agg
      )
    }));
  }, []);

  const removeAggregation = useCallback((index: number) => {
    setQuery(prev => ({
      ...prev,
      aggregations: (prev.aggregations || []).filter((_, i) => i !== index)
    }));
  }, []);

  const handleDateRangeChange = useCallback((field: 'start' | 'end', value: string) => {
    setQuery(prev => ({
      ...prev,
      dateRange: {
        ...prev.dateRange,
        field: prev.dateRange?.field || 'createdAt',
        [field]: value || undefined
      }
    }));
  }, []);

  const runQuery = async () => {
    if (!query.entity) {
      onError('Please select an entity type');
      return;
    }

    setLoading(true);
    try {
      const result = await reportingService.createReport(query);
      onReportGenerated(result);
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (format: 'json' | 'csv' | 'xlsx') => {
    if (!query.entity) {
      onError('Please configure your query first');
      return;
    }

    setLoading(true);
    try {
      const blob = await reportingService.exportReport(query, format);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `report_${query.entity}_${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Failed to export report');
    } finally {
      setLoading(false);
    }
  };

  const loadSuggestedFilters = () => {
    const suggestions = reportingService.getSuggestedFilters(query.entity);
    setQuery(prev => ({
      ...prev,
      filters: suggestions.map(s => ({ field: s.field || '', operator: s.operator || 'equals', value: '' }))
    }));
  };

  const loadAISuggestions = async () => {
    try {
      const response = await reportingService.getAIQuerySuggestions();
      setAiSuggestions(response.suggestions);
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Failed to load AI suggestions');
    }
  };

  const handleAIQueryGenerated = useCallback((aiQuery: ReportQuery, explanation: string) => {
    setQuery(aiQuery);
    setAiExplanation(explanation);
    setActiveTab(1); // Switch to manual tab to show the generated query
  }, []);

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Query Builder
        </Typography>

        {/* AI/Manual Toggle */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            aria-label="query builder mode"
          >
            <Tab
              icon={<AIIcon />}
              label="AI Assistant"
              id="tab-0"
              aria-controls="tabpanel-0"
            />
            <Tab
              icon={<ManualIcon />}
              label="Manual Builder"
              id="tab-1"
              aria-controls="tabpanel-1"
            />
          </Tabs>
        </Box>

        {/* AI Assistant Tab */}
        {activeTab === 0 && (
          <Box role="tabpanel" id="tabpanel-0" aria-labelledby="tab-0">
            <AIQueryAssistant
              onQueryGenerated={handleAIQueryGenerated}
              onError={onError}
              suggestions={aiSuggestions}
              onLoadSuggestions={loadAISuggestions}
            />
          </Box>
        )}

        {/* Manual Builder Tab */}
        {activeTab === 1 && (
          <Box role="tabpanel" id="tabpanel-1" aria-labelledby="tab-1">
            {/* AI Explanation if available */}
            {aiExplanation && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'success.50', borderRadius: 1, border: 1, borderColor: 'success.200' }}>
                <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                  <AIIcon color="success" fontSize="small" />
                  <Typography variant="subtitle2" color="success.main">
                    AI-Generated Query
                  </Typography>
                </Box>
                <Typography variant="body2" color="success.dark">
                  {aiExplanation}
                </Typography>
              </Box>
            )}
            
            {/* Entity Selection */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Data Source</InputLabel>
                  <Select
                    value={query.entity}
                    label="Data Source"
                    onChange={(e) => setQuery(prev => ({ 
                      ...prev, 
                      entity: e.target.value as 'form_submissions' | 'clients' | 'loans'
                    }))}
                  >
                    {entityOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Button
                  variant="outlined"
                  onClick={loadSuggestedFilters}
                  disabled={!query.entity}
                >
                  Load Suggested Filters
                </Button>
              </Grid>
            </Grid>

        {/* Filters */}
        <Box sx={{ mb: 3 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Filters</Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={addFilter}
              size="small"
              disabled={!query.entity}
            >
              Add Filter
            </Button>
          </Box>
          
          {query.filters.map((filter, index) => (
            <Paper key={index} sx={{ p: 2, mb: 1 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, sm: 3 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Field</InputLabel>
                    <Select
                      value={filter.field}
                      label="Field"
                      onChange={(e) => updateFilter(index, { field: e.target.value })}
                    >
                      {availableFields.map(field => (
                        <MenuItem key={field.field} value={field.field}>
                          {field.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Operator</InputLabel>
                    <Select
                      value={filter.operator}
                      label="Operator"
                      onChange={(e) => updateFilter(index, { 
                        operator: e.target.value as QueryFilter['operator']
                      })}
                    >
                      {operatorOptions.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 5 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Value"
                    value={filter.value}
                    onChange={(e) => updateFilter(index, { value: e.target.value })}
                    placeholder={
                      filter.operator === 'between' ? 'min,max' :
                      filter.operator === 'in' || filter.operator === 'not_in' ? 'value1,value2,value3' :
                      'Enter value'
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 1 }}>
                  <IconButton onClick={() => removeFilter(index)} size="small" color="error">
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Paper>
          ))}
        </Box>

        {/* Date Range */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>Date Range Filter</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel>Date Field</InputLabel>
                <Select
                  value={query.dateRange?.field || ''}
                  label="Date Field"
                  onChange={(e) => setQuery(prev => ({
                    ...prev,
                    dateRange: { ...prev.dateRange, field: e.target.value }
                  }))}
                >
                  {availableFields.filter(f => f.type === 'date').map(field => (
                    <MenuItem key={field.field} value={field.field}>
                      {field.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={query.dateRange?.start ? query.dateRange.start.split('T')[0] : ''}
                onChange={(e) => handleDateRangeChange('start', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                value={query.dateRange?.end ? query.dateRange.end.split('T')[0] : ''}
                onChange={(e) => handleDateRangeChange('end', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Advanced Options Toggle */}
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={showAdvanced}
                onChange={(e) => setShowAdvanced(e.target.checked)}
              />
            }
            label="Show Advanced Options"
          />
        </Box>

        {/* Advanced Options */}
        {showAdvanced && (
          <>
            <Divider sx={{ my: 2 }} />
            
            {/* Group By */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>Group By</Typography>
              <FormControl fullWidth>
                <InputLabel>Group By Fields</InputLabel>
                <Select
                  multiple
                  value={query.groupBy || []}
                  label="Group By Fields"
                  onChange={(e) => setQuery(prev => ({ ...prev, groupBy: e.target.value as string[] }))}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((value) => (
                        <Chip key={value} label={availableFields.find(f => f.field === value)?.label || value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {availableFields.map(field => (
                    <MenuItem key={field.field} value={field.field}>
                      {field.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Aggregations */}
            <Box sx={{ mb: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="subtitle1">Aggregations</Typography>
                <Button
                  startIcon={<AddIcon />}
                  onClick={addAggregation}
                  size="small"
                >
                  Add Aggregation
                </Button>
              </Box>
              
              {(query.aggregations || []).map((agg, index) => (
                <Paper key={index} sx={{ p: 2, mb: 1 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, sm: 5 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Field</InputLabel>
                        <Select
                          value={agg.field}
                          label="Field"
                          onChange={(e) => updateAggregation(index, { field: e.target.value })}
                        >
                          {availableFields.filter(f => f.type === 'number').map(field => (
                            <MenuItem key={field.field} value={field.field}>
                              {field.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 5 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Function</InputLabel>
                        <Select
                          value={agg.function}
                          label="Function"
                          onChange={(e) => updateAggregation(index, { function: e.target.value })}
                        >
                          {aggregationOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 2 }}>
                      <IconButton onClick={() => removeAggregation(index)} size="small" color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Box>

            {/* Sorting and Pagination */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth>
                  <InputLabel>Sort By</InputLabel>
                  <Select
                    value={query.sortBy?.field || ''}
                    label="Sort By"
                    onChange={(e) => setQuery(prev => ({
                      ...prev,
                      sortBy: { 
                        field: e.target.value,
                        direction: prev.sortBy?.direction || 'asc'
                      }
                    }))}
                  >
                    {availableFields.map(field => (
                      <MenuItem key={field.field} value={field.field}>
                        {field.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth>
                  <InputLabel>Sort Direction</InputLabel>
                  <Select
                    value={query.sortBy?.direction || 'asc'}
                    label="Sort Direction"
                    onChange={(e) => setQuery(prev => ({
                      ...prev,
                      sortBy: { 
                        field: prev.sortBy?.field || '',
                        direction: e.target.value as 'asc' | 'desc'
                      }
                    }))}
                  >
                    <MenuItem value="asc">Ascending</MenuItem>
                    <MenuItem value="desc">Descending</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Limit Results"
                  type="number"
                  value={query.limit || ''}
                  onChange={(e) => setQuery(prev => ({ ...prev, limit: Number(e.target.value) || undefined }))}
                  inputProps={{ min: 1, max: 10000 }}
                />
              </Grid>
            </Grid>
          </>
        )}
          </Box>
        )}

        {/* Actions - shown for both tabs when manual tab is active or when query is ready */}
        {(activeTab === 1 || (activeTab === 0 && query.entity)) && (
          <Box display="flex" gap={2} flexWrap="wrap" sx={{ mt: 2 }}>
            <Button
              variant="contained"
              startIcon={<RunIcon />}
              onClick={runQuery}
              disabled={loading || !query.entity}
            >
              {loading ? 'Running Query...' : 'Run Query'}
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<ExportIcon />}
              onClick={() => exportReport('csv')}
              disabled={loading || !query.entity}
            >
              Export CSV
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<ExportIcon />}
              onClick={() => exportReport('json')}
              disabled={loading || !query.entity}
            >
              Export JSON
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default QueryBuilder;